import { create } from "zustand";

export type Message =
  | { id: string; kind: "ai-greeting"; text: string; createdAt: number }
  | { id: string; kind: "ai-question"; questionId: string; text: string; createdAt: number }
  | { id: string; kind: "ai-confirm"; questionId: string; text: string; createdAt: number }
  | { id: string; kind: "ai-summary"; text: string; createdAt: number }
  | { id: string; kind: "ai-resume-prompt"; text: string; createdAt: number }
  | { id: string; kind: "user-transcript"; questionId: string; text: string; createdAt: number };

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
type AppendInput = DistributiveOmit<Message, "id" | "createdAt">;

type State = {
  messages: Message[];
  sessionId: string | null;
  turnIndex: number;
};

type Actions = {
  append: (msg: AppendInput) => string;
  updateText: (id: string, text: string) => void;
  removeAfter: (id: string) => void;
  removeUserTranscript: (questionId: string) => void;
  removeMessage: (id: string) => void;
  clear: () => void;
  startSession: (args: { sessionId: string }) => void;
  nextTurn: () => void;
};

let counter = 0;
function nextId(): string {
  counter += 1;
  return `msg-${counter}-${Date.now()}`;
}

export const useChatStore = create<State & Actions>((set, get) => ({
  messages: [],
  sessionId: null,
  turnIndex: 0,

  append: (msg) => {
    const id = nextId();
    const full = { ...msg, id, createdAt: Date.now() } as Message;
    set({ messages: [...get().messages, full] });
    return id;
  },

  updateText: (id, text) => {
    set({
      messages: get().messages.map((m) => (m.id === id ? ({ ...m, text } as Message) : m)),
    });
  },

  removeAfter: (id) => {
    const idx = get().messages.findIndex((m) => m.id === id);
    if (idx < 0) return;
    set({ messages: get().messages.slice(0, idx + 1) });
  },

  removeUserTranscript: (questionId) => {
    const msgs = get().messages;
    const idx = msgs.findIndex((m) => m.kind === "user-transcript" && m.questionId === questionId);
    if (idx < 0) return;
    set({ messages: msgs.slice(0, idx) });
  },

  // 指定した 1 件だけを消す。removeUserTranscript (slice で以降を全部消す) とは別物で、
  // 読み上げ中の割り込みを「回答ではなかった」と判定したときに、先に append した
  // 吹き出しを取り消すのに使う (spec 2026-07-28 §3.6(3))。
  // あちらを流用すると、過去に同じ設問へ答えていた場合にそこから先が全部消える。
  removeMessage: (id) => {
    set({ messages: get().messages.filter((m) => m.id !== id) });
  },

  clear: () => set({ messages: [], sessionId: null, turnIndex: 0 }),

  startSession: ({ sessionId }) => set({ sessionId, turnIndex: 0 }),

  nextTurn: () => set({ turnIndex: get().turnIndex + 1 }),
}));
